import { settingsConfig, getDefaultSettings } from '../config/settingsConfig.js';

/**
 * Settings Service
 * Handles localStorage operations for settings
 */

class SettingsService {
  constructor() {
    this.storageKey = 'strava_pwa_settings';
  }

  /**
   * Get all settings from localStorage
   */
  getAllSettings() {
    try {
      // Always start with defaults
      const defaults = getDefaultSettings();
      console.log('Default settings:', defaults);
      
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('Stored settings:', parsed);
        
        // Filter stored settings to only include known config keys
        const validStoredSettings = {};
        const settingsToRemove = [];
        
        Object.keys(parsed).forEach(key => {
          if (defaults.hasOwnProperty(key)) {
            // Check if the stored value is different from default
            if (parsed[key] !== defaults[key]) {
              validStoredSettings[key] = parsed[key];
            } else {
              console.log(`Removing setting ${key} - matches default value`);
              settingsToRemove.push(key);
            }
          } else {
            console.log(`Removing unknown setting: ${key}`);
            settingsToRemove.push(key);
          }
        });
        
        console.log('Valid stored settings:', validStoredSettings);
        console.log('Settings to remove:', settingsToRemove);
        
        // Clean up localStorage if needed
        if (settingsToRemove.length > 0) {
          const cleanedSettings = { ...parsed };
          settingsToRemove.forEach(key => {
            delete cleanedSettings[key];
          });
          localStorage.setItem(this.storageKey, JSON.stringify(cleanedSettings));
          console.log('Cleaned localStorage, removed:', settingsToRemove);
        }
        
        // Merge valid stored settings with defaults
        const merged = { ...defaults, ...validStoredSettings };
        console.log('Merged settings:', merged);
        return merged;
      }
      
      console.log('No stored settings, returning defaults');
      return defaults;
    } catch (error) {
      console.error('Error loading settings:', error);
      return getDefaultSettings();
    }
  }

  /**
   * Get a specific setting
   */
  getSetting(key) {
    const settings = this.getAllSettings();
    return settings[key];
  }

  /**
   * Save all settings to localStorage
   */
  saveAllSettings(settings) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
      return true;
    } catch (error) {
      console.error('Error saving settings:', error);
      return false;
    }
  }

  /**
   * Save a specific setting
   */
  saveSetting(key, value) {
    const settings = this.getAllSettings();
    settings[key] = value;
    return this.saveAllSettings(settings);
  }

  /**
   * Reset settings to defaults
   */
  resetSettings() {
    const defaults = getDefaultSettings();
    return this.saveAllSettings(defaults);
  }

  /**
   * Clear all settings
   */
  clearSettings() {
    try {
      localStorage.removeItem(this.storageKey);
      return true;
    } catch (error) {
      console.error('Error clearing settings:', error);
      return false;
    }
  }

  /**
   * Get settings metadata for form generation
   */
  getSettingsMetadata() {
    return Object.values(settingsConfig).map(setting => ({
      key: setting.key,
      description: setting.description,
      type: setting.type,
      defaultValue: setting.defaultValue,
      validation: setting.validation
    }));
  }
}

const settingsService = new SettingsService();
export default settingsService;
